/**
 * task-thread-client.ts — REST client for Spec 017 task-thread endpoints.
 *
 * Talks to equanaut-master's /api/companies/{companyId}/issues and
 * /api/companies/{companyId}/worktrees endpoints.
 *
 * Uses browser credentials (Better-Auth session cookie) per FR-016.
 *
 * Spec 017 FR-005, FR-006, FR-012, FR-016.
 */

import type {
  IssueShape,
  TaskThreadListResponse,
  WorktreeRegisterResponse,
  SurfaceKind,
} from '@logic/types/task-thread'

const EQUANAUT_BASE_URL: string =
  (typeof process !== 'undefined' && (process.env as any).REACT_APP_EQUANAUT_BASE_URL) ||
  ''

function apiUrl(path: string): string {
  return `${EQUANAUT_BASE_URL}${path}`
}

// ---------------------------------------------------------------------------
// Issues list (paginated)
// ---------------------------------------------------------------------------

export async function listIssues(
  companyId: string,
  cursor?: string | null,
  filter?: 'all' | 'mine' | 'active' | 'archived',
  limit?: number
): Promise<TaskThreadListResponse> {
  const params = new URLSearchParams()
  if (cursor) params.set('cursor', cursor)
  if (filter) params.set('filter', filter)
  if (limit) params.set('limit', String(Math.min(limit, 100)))

  const qs = params.toString()
  const url = apiUrl(
    `/api/companies/${companyId}/issues${qs ? `?${qs}` : ''}`
  )
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`[task-thread-client] listIssues ${res.status}: ${res.statusText}`)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Get single issue (4-layer anatomy)
// ---------------------------------------------------------------------------

export async function getIssue(
  companyId: string,
  issueId: string
): Promise<IssueShape> {
  const res = await fetch(
    apiUrl(`/api/companies/${companyId}/issues/${issueId}`),
    {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }
  )
  if (!res.ok) {
    throw new Error(`[task-thread-client] getIssue ${res.status}: ${res.statusText}`)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// PATCH issue status
// ---------------------------------------------------------------------------

export async function patchIssueStatus(
  companyId: string,
  issueId: string,
  status: IssueShape['status'],
  worktreeId?: string | null
): Promise<IssueShape> {
  const res = await fetch(
    apiUrl(`/api/companies/${companyId}/issues/${issueId}/status`),
    {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(worktreeId ? { 'X-Worktree-Id': worktreeId } : {}),
      },
      body: JSON.stringify({ status }),
    }
  )
  if (!res.ok) {
    throw new Error(`[task-thread-client] patchIssueStatus ${res.status}: ${res.statusText}`)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Worktree registration (FR-012, FR-029)
// ---------------------------------------------------------------------------

export async function attachWorktree(
  companyId: string,
  issueId: string,
  surfaceKind: SurfaceKind,
  surfaceIdentity: string
): Promise<WorktreeRegisterResponse> {
  const res = await fetch(
    apiUrl(`/api/companies/${companyId}/issues/${issueId}/worktrees`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ surfaceKind, surfaceIdentity }),
    }
  )
  if (!res.ok) {
    throw new Error(`[task-thread-client] attachWorktree ${res.status}: ${res.statusText}`)
  }
  return res.json()
}

export async function detachWorktree(
  companyId: string,
  issueId: string,
  worktreeId: string
): Promise<void> {
  const res = await fetch(
    apiUrl(`/api/companies/${companyId}/issues/${issueId}/worktrees/${worktreeId}`),
    {
      method: 'DELETE',
      credentials: 'include',
    }
  )
  if (!res.ok && res.status !== 404) {
    throw new Error(`[task-thread-client] detachWorktree ${res.status}: ${res.statusText}`)
  }
}

// ---------------------------------------------------------------------------
// Surface-level worktree registration (POST /api/companies/{cid}/worktrees)
// Called on first WS subscription to identify this surface (FR-012, FR-029)
// ---------------------------------------------------------------------------

export interface SurfaceWorktreeResponse {
  worktreeId: string
  lastEventId: string | null
}

export async function registerSurfaceWorktree(
  companyId: string,
  surfaceKind: SurfaceKind,
  surfaceIdentity: string
): Promise<SurfaceWorktreeResponse> {
  const res = await fetch(
    apiUrl(`/api/companies/${companyId}/worktrees`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        surfaceKind,
        surfaceIdentity,
      }),
    }
  )
  if (!res.ok) {
    // Non-fatal — surface still renders snapshot even if worktree registration fails
    console.warn('[task-thread-client] registerSurfaceWorktree failed:', res.status)
    return { worktreeId: '', lastEventId: null }
  }
  const data = await res.json()
  const worktree = data.worktree ?? data
  return {
    worktreeId: worktree?.id ?? data.worktreeId ?? '',
    lastEventId: data.lastEventId ?? null,
  }
}
